import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { Package } from '@renderer/types/configState'
import { IoPrint } from 'react-icons/io5'
import { MdPhotoLibrary } from 'react-icons/md'
import { DeepPartial, SessionStateData } from '@renderer/types/sessionState'
import { useTranslation } from 'react-i18next'
import NewspaperLayout from './components/NewspaperLayout'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    id,
    setSessionState,
    transaction,
    userPreferences,
    mode,
    logActivity,
    uploadSessionState
  } = useSessionStateStore()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const paymentConfig = globalConfig?.pages.payment
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const welcomeSettings = getPageSettings('welcome')
  const [showPromoInput, setShowPromoInput] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>('')

  // Memoize packages calculations (same as default)
  const packages = useMemo(() => config?.settings.packages || [], [config])
  const printPackages = useMemo(
    () =>
      mode === 'capture'
        ? packages
            .filter((pkg) => pkg.type === 'print')
            .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        : [],
    [mode, packages]
  )
  const gifPackage = useMemo(
    () => (mode === 'capture' ? packages.find((pkg) => pkg.type === 'gif') : null),
    [mode, packages]
  )
  const recordingPackage = useMemo(
    () =>
      mode === 'capture'
        ? packages.find(
            (pkg) =>
              pkg.type === 'recording' ||
              pkg.type === 'strip_live' ||
              pkg.type === 'strip_story' ||
              pkg.type === 'session_recording'
          )
        : null,
    [mode, packages]
  )
  const videoPackage = useMemo(
    () => (mode === 'record' ? packages.find((pkg) => pkg.type === 'video') : null),
    [mode, packages]
  )
  const digitalPackage = useMemo(() => packages.find((pkg) => pkg.type === 'digital'), [packages])
  const paxPackages = useMemo(
    () =>
      mode === 'capture'
        ? packages
            .filter((pkg) => pkg.type === 'pax')
            .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        : [],
    [mode, packages]
  )

  const recordingType = useMemo(() => {
    if (!recordingPackage) return null
    if (recordingPackage.type === 'recording') return 'strip_live' as const
    return recordingPackage.type as 'strip_live' | 'strip_story' | 'session_recording'
  }, [recordingPackage])

  // Initial navigation checks (same as default)
  useEffect(() => {
    if (
      mode === 'capture' &&
      !printPackages.length &&
      !gifPackage &&
      !recordingPackage &&
      !digitalPackage
    ) {
      const target =
        welcomeSettings?.startWithPayment && paymentConfig?.settings.enabled ? 'payment' : 'capture'
      navigate(`/${projectId}/${target}`)
    }
    if (mode === 'record' && !videoPackage) {
      navigate(`/${projectId}/record`)
    }
  }, [
    mode,
    printPackages,
    gifPackage,
    recordingPackage,
    videoPackage,
    navigate,
    projectId,
    digitalPackage,
    welcomeSettings,
    paymentConfig
  ])

  const [selectedPrintQuantity, setSelectedPrintQuantity] = useState<number>(
    printPackages.find((pkg) => pkg.quantity >= 1)?.quantity || 1
  )

  useEffect(() => {
    if (config?.settings.packages?.length && !transaction.selectedPackages.length) {
      Promise.resolve().then(() => {
        const hasPrint = printPackages.length > 0
        const initialDigital = digitalPackage ? !digitalPackage.optional : !hasPrint
        const initialPax = paxPackages.length > 0

        setSessionState({
          userPreferences: {
            copies: selectedPrintQuantity,
            useGIF: gifPackage ? !gifPackage.optional : false,
            useRecording: recordingPackage ? !recordingPackage.optional : false,
            useVideo: videoPackage ? !videoPackage.optional : false,
            useDigital: initialDigital,
            usePrint: hasPrint,
            usePax: initialPax,
            recordingType: recordingType as
              | 'recording'
              | 'strip_live'
              | 'strip_story'
              | 'session_recording'
              | null
              | undefined
          }
        })
      })
    }
  }, [
    config,
    packages,
    selectedPrintQuantity,
    gifPackage,
    recordingPackage,
    videoPackage,
    recordingType,
    digitalPackage,
    printPackages.length,
    paxPackages.length
  ])

  const getSelectedPrintPackage = (): Package | undefined => {
    return printPackages.find((pkg) => pkg.quantity === selectedPrintQuantity)
  }

  const calculateSubtotal = (): number => {
    let subtotal = 0
    if (paymentConfig?.settings.enabled) {
      if (mode === 'capture') {
        if (printPackages.length > 0) {
          const selectedPrintPackage = getSelectedPrintPackage()
          subtotal += Math.round(selectedPrintPackage?.price || 0)
        }
        if (gifPackage && userPreferences.useGIF) subtotal += Math.round(gifPackage.price)
        if (recordingPackage && userPreferences.useRecording)
          subtotal += Math.round(recordingPackage.price)
        if (digitalPackage && userPreferences.useDigital) {
          subtotal += Math.round(digitalPackage.price)
        }
      } else if (mode === 'record' && videoPackage && userPreferences.useVideo) {
        subtotal += Math.round(videoPackage.price)
      }
    }
    return Math.round(subtotal)
  }

  const calculateTotal = (): number => {
    const subtotal = calculateSubtotal()
    const discount = Math.round(transaction.discount || 0)
    return Math.round(Math.max(0, subtotal - discount))
  }

  const handleNext = async (): Promise<void> => {
    if (!transaction.selectedPackages) return

    const subtotalForNext = calculateSubtotal()
    const discountForNext = Math.round(transaction.discount || 0)
    const amountToSet =
      transaction.status === 'settlement'
        ? transaction.amount ?? subtotalForNext
        : Math.round(Math.max(0, subtotalForNext - discountForNext))

    const packagesInCalculation: Package[] = []
    if (mode === 'capture') {
      if (printPackages.length > 0) {
        const selectedPrintPackage = getSelectedPrintPackage()
        if (selectedPrintPackage) packagesInCalculation.push(selectedPrintPackage)
      }
      if (gifPackage && userPreferences.useGIF && gifPackage) {
        packagesInCalculation.push(gifPackage)
      }
      if (recordingPackage && userPreferences.useRecording && recordingPackage) {
        packagesInCalculation.push(recordingPackage)
      }
      if (digitalPackage && userPreferences.useDigital && digitalPackage) {
        packagesInCalculation.push(digitalPackage)
      }
    } else if (mode === 'record' && videoPackage && userPreferences.useVideo && videoPackage) {
      packagesInCalculation.push(videoPackage)
    }

    const details = {
      copies: selectedPrintQuantity,
      amount: amountToSet,
      package: packagesInCalculation.map((pkg) => pkg.type).join(', '),
      packageDescription: packagesInCalculation.map((pkg) => pkg.description).join(', '),
      promoApplied: Boolean(promoCode),
      promoCode: promoCode || undefined
    }
    logActivity('Checkout complete.', details)

    setSessionState({
      transaction: {
        ...(transaction.status !== 'settlement' ? { amount: amountToSet } : {}),
        selectedPackages: packagesInCalculation
      }
    })

    await Promise.resolve()

    setTimeout(() => {
      uploadSessionState(id)
    }, 5000)

    // Check applyAfterCapture setting
    const filterSettings = getPageSettings('filter')
    const applyAfterCapture = filterSettings?.applyAfterCapture || false

    // If transaction status is already settlement (cash payment), skip payment page
    if (transaction.status === 'settlement') {
      const nextPage = welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : mode === 'capture'
          ? 'capture'
          : 'record'
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    // Navigate based on startWithPayment setting, applyAfterCapture, and mode
    const nextPage = paymentConfig?.settings.enabled
      ? calculateTotal() === 0
        ? welcomeSettings?.startWithPayment
          ? applyAfterCapture
            ? 'frame'
            : 'filter'
          : mode === 'capture'
            ? 'capture'
            : 'record'
        : 'payment'
      : welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : mode === 'capture'
          ? 'capture'
          : 'record'
    navigate(`/${projectId}/${nextPage}`)
  }

  const checkoutTimeout = config?.settings?.checkoutTimeout || 0
  const countdown = useCountdown(checkoutTimeout)

  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      countdown.setOnComplete(() => {
        const details = {
          selectedPackages: transaction.selectedPackages?.map((pkg) => pkg.type),
          amount: calculateTotal(),
          timeoutDuration: checkoutTimeout
        }
        logActivity('Checkout timed out.', details)
        handleNext()
      })
    }
  }, [
    checkoutTimeout,
    transaction.selectedPackages,
    setSessionState,
    transaction.discount,
    handleNext
  ])

  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [checkoutTimeout])

  const handlePromoEnter = async (code: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code,
          projectId,
          amount: Math.round(calculateSubtotal())
        })
      })

      const data = await response.json()
      const isValid = response.ok && data.isValid
      const details = {
        code,
        message: data.message,
        isCash: data.isCash
      }
      logActivity(
        isValid
          ? `Promo code "${code}" applied successfully.`
          : `Failed to apply promo code "${code}".`,
        details
      )

      const subtotal = calculateSubtotal()
      const apiDiscount = Math.round(data.discount || 0)
      const isCash = Boolean(isValid && data.isCash)
      const finalAmount = isValid
        ? isCash
          ? Math.round(subtotal)
          : Math.round(subtotal - apiDiscount)
        : undefined
      const calculatedDiscount = isValid ? (isCash ? 0 : apiDiscount) : undefined

      const updates: DeepPartial<SessionStateData> = {
        transaction: {
          amount: finalAmount,
          discount: isValid ? calculatedDiscount : undefined,
          voucherCode: isValid ? code : undefined,
          voucherId: isValid && data.promoId ? data.promoId : undefined,
          voucherType:
            isValid && data.isCash ? 'cash' : isValid && data.type ? data.type : undefined,
          voucherValue: isValid && data.value != null ? data.value : undefined,
          status: isValid && (data.isCash || finalAmount === 0) ? 'settlement' : undefined
        }
      }

      setSessionState(updates)

      if (isValid) {
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowPromoInput(false)
      } else {
        cogoToast.error(data.message || t('checkout.invalidPromo', 'Invalid promo code'))
      }
    } catch (error) {
      console.error('Error validating promo code:', error)
      cogoToast.error(
        t('checkout.promoValidationFailed', 'Failed to validate promo code. Please try again.')
      )
    }
  }

  // Once a voucher/promo has fully settled the transaction (cash voucher or
  // 100% discount), the paid amount and package selection must stay frozen.
  // Otherwise the customer can add/increase items (e.g. print copies) for
  // free after the session is already marked as paid, since there is no
  // further payment step to collect the difference. Production evidence:
  // sessions ub2y47 (amount inflated past the voucher cap) and f27g86
  // (copies incremented past what the voucher covered).
  const isOrderLockedByVoucher = (): boolean => {
    if (transaction.status === 'settlement') {
      cogoToast.warn(
        t(
          'checkout.orderLockedByVoucher',
          'Remove the applied voucher to change your order.'
        )
      )
      return true
    }
    return false
  }

  const handlePackageChange = (
    packageType: string,
    value: boolean | number,
    packageInfo: Package
  ): void => {
    if (isOrderLockedByVoucher()) return

    const details = {
      packageType,
      value,
      price: packageInfo.price
    }
    logActivity(`Package selection changed for ${packageInfo.description || packageType}.`, details)

    const updates: DeepPartial<SessionStateData> = {
      userPreferences: {
        [packageType === 'print'
          ? 'copies'
          : packageType === 'gif'
            ? 'useGIF'
            : packageType === 'digital'
              ? 'useDigital'
              : packageType === 'pax'
                ? 'usePax'
                : `use${packageType.charAt(0).toUpperCase() + packageType.slice(1)}`]: value
      },
      transaction: {
        selectedPackages: value
          ? [...(transaction?.selectedPackages || []), packageInfo]
          : (transaction?.selectedPackages || []).filter((pkg) => pkg.type !== packageType),
        amount: calculateTotal()
      }
    }

    setSessionState(updates)
  }

  const selectedPrintPackage = getSelectedPrintPackage()

  return (
    <NewspaperLayout disableOverflow={true}>
      <div className="flex flex-col h-full relative overflow-hidden">
        <div className="flex flex-col items-center bg-newspaper-white w-full mx-auto border-2 border-double border-[#2a2a2a] gap-4 p-12">
          <h1 className="font-newspaper font-bold text-6xl px-2 text-center leading-tight uppercase tracking-wider text-[#1a1a1a] mb-4">
            {t('checkout.title', 'Review Your Order')}
          </h1>

          <div className="flex flex-col items-stretch gap-6 w-full">
            {/* Print Package Adjustment */}
            {mode === 'capture' && printPackages.length > 0 && (
              <div className="border-2 border-double border-[#2a2a2a] flex items-center justify-between w-full py-3 px-6">
                <div className="flex items-center gap-4">
                  <div className="text-newspaper-black font-medium border-2 border-double border-[#2a2a2a] flex items-center justify-center text-7xl w-24 h-24 p-3 rounded-none">
                    <IoPrint />
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-newspaper-black font-medium text-3xl">
                      {t('checkout.print', 'Print')}
                    </h2>
                    <span className="text-newspaper-black text-2xl">
                      {t('checkout.choosePrints', 'Choose number of prints')}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <div className="flex items-center border border-[#2a2a2a] rounded-none">
                    <button
                      type="button"
                      title={t('checkout.minus', 'Minus')}
                      className="hover:bg-gray-200 text-newspaper-black font-bold border-r border-[#2a2a2a] flex items-center justify-center text-3xl w-16 h-16 rounded-none"
                      onClick={() => {
                        if (isOrderLockedByVoucher()) return
                        const minQuantity =
                          printPackages.length > 0
                            ? Math.min(...printPackages.map((p) => p.quantity))
                            : 1
                        const newQuantity = Math.max(minQuantity, selectedPrintQuantity - 1)
                        const newPackage = printPackages.find((pkg) => pkg.quantity === newQuantity)
                        if (newPackage) {
                          handlePackageChange('print', newQuantity, newPackage)
                          setSelectedPrintQuantity(newQuantity)
                        }
                      }}
                    >
                      -
                    </button>
                    <span className="px-4 font-medium flex items-center justify-center text-3xl w-16 h-16">
                      {selectedPrintQuantity}
                    </span>
                    <button
                      type="button"
                      title={t('checkout.plus', 'Plus')}
                      className="hover:bg-gray-300 text-newspaper-black font-bold border-l border-[#2a2a2a] flex items-center justify-center text-3xl w-16 h-16 rounded-none"
                      onClick={() => {
                        if (isOrderLockedByVoucher()) return
                        const maxQuantity = printPackages[printPackages.length - 1].quantity
                        const newQuantity = Math.min(maxQuantity, selectedPrintQuantity + 1)
                        const newPackage =
                          printPackages.find((pkg) => pkg.quantity === newQuantity) ||
                          printPackages[0]
                        handlePackageChange('print', newQuantity, newPackage)
                        setSelectedPrintQuantity(newQuantity)
                      }}
                    >
                      +
                    </button>
                  </div>
                  {selectedPrintPackage?.description && (
                    <span className="text-gray-500 mt-1 text-xl">
                      {selectedPrintPackage.description}
                    </span>
                  )}
                </div>

                <span className="text-newspaper-black text-3xl">
                  {selectedPrintPackage?.price && selectedPrintPackage.price > 0
                    ? formatAmount(selectedPrintPackage.price, projectCurrency)
                    : t('checkout.free', 'Free')}
                </span>
              </div>
            )}

            {/* Digital Photos Package */}
            {mode === 'capture' && printPackages.length === 0 && digitalPackage && (
              <div className="border-2 border-double border-[#2a2a2a] flex items-center w-full py-3 px-6">
                <div className="flex items-center gap-4">
                  <div className="text-newspaper-black font-medium border-2 border-double border-[#2a2a2a] flex items-center justify-center text-3xl w-24 h-24 p-3 rounded-none">
                    <MdPhotoLibrary />
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-newspaper-black font-medium text-xl">
                      {t('checkout.digital', 'Digital Photos')}
                    </h2>
                    <span className="text-newspaper-black text-lg">
                      {t('checkout.getDigital', 'Get digital copies of your photos')}
                    </span>
                  </div>
                </div>
                <div className="flex-grow flex justify-center">
                  <input
                    title={t('checkout.digital', 'Digital Photos')}
                    type="checkbox"
                    className={`w-6 h-6 cursor-pointer ${
                      !digitalPackage.optional ? 'invisible' : ''
                    }`}
                    checked={userPreferences.useDigital}
                    onChange={() =>
                      digitalPackage &&
                      handlePackageChange(
                        'digital',
                        !userPreferences.useDigital,
                        digitalPackage
                      )
                    }
                  />
                </div>
                <span
                  className={`text-newspaper-black ml-auto text-xl ${
                    !userPreferences.useDigital && digitalPackage.optional ? 'opacity-50' : ''
                  }`}
                >
                  {digitalPackage.price > 0
                    ? formatAmount(digitalPackage.price, projectCurrency)
                    : t('checkout.free', 'Free')}
                </span>
              </div>
            )}

            {/* GIF Package */}
            {mode === 'capture' && gifPackage && (
              <div className="border-2 border-double border-[#2a2a2a] flex items-center w-full py-3 px-6">
                <div className="flex items-center gap-4">
                  <div className="text-newspaper-black font-medium border-2 border-double border-[#2a2a2a] flex items-center justify-center text-7xl w-24 h-24 p-3 rounded-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2M6 4h5v8l-2.5-1.5L6 12zm7 0h5v8l-2.5-1.5L13 12z"/>
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-newspaper-black font-medium text-3xl">{t('checkout.gif', 'GIF')}</h2>
                    <span className="text-newspaper-black text-2xl">
                      {t('checkout.getAnimated', 'Get animated image')}
                    </span>
                  </div>
                </div>
                <div className="flex-grow flex justify-center">
                  <input
                    title={t('checkout.gif', 'GIF')}
                    type="checkbox"
                    className={`w-6 h-6 cursor-pointer ${!gifPackage.optional ? 'invisible' : ''}`}
                    checked={userPreferences.useGIF}
                    onChange={() =>
                      gifPackage && handlePackageChange('gif', !userPreferences.useGIF, gifPackage)
                    }
                  />
                </div>
                <span
                  className={`text-newspaper-black ml-auto text-3xl ${
                    !userPreferences.useGIF && gifPackage.optional ? 'opacity-50' : ''
                  }`}
                >
                  {gifPackage.price > 0
                    ? formatAmount(gifPackage.price, projectCurrency)
                    : t('checkout.free', 'Free')}
                </span>
              </div>
            )}

            {/* Recording Package */}
            {mode === 'capture' && recordingPackage && (
              <div className="border-2 border-double border-[#2a2a2a] flex items-center w-full py-3 px-6">
                <div className="flex items-center gap-4">
                  <div className="text-newspaper-black font-medium border-2 border-double border-[#2a2a2a] flex items-center justify-center text-7xl w-24 h-24 p-3 rounded-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11z"/>
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-newspaper-black font-medium text-3xl">
                      {t('recording.title')} -{' '}
                      {t(
                        `recording.modes.${
                          recordingType === 'strip_live'
                            ? 'live'
                            : recordingType === 'strip_story'
                              ? 'story'
                              : recordingType === 'session_recording'
                                ? 'session'
                                : 'live'
                        }`
                      )}
                    </h2>
                    <span className="text-newspaper-black text-2xl">
                      {recordingType === 'strip_live'
                        ? t('recording.description.strip_live')
                        : recordingType === 'strip_story'
                          ? t('recording.description.strip_story')
                          : recordingType === 'session_recording'
                            ? t('recording.description.session_recording')
                            : t('recording.description.strip_live')}
                    </span>
                  </div>
                </div>
                <div className="flex-grow flex justify-center">
                  <input
                    title={t('recording.title')}
                    type="checkbox"
                    className={`w-6 h-6 cursor-pointer ${!recordingPackage.optional ? 'invisible' : ''}`}
                    checked={userPreferences.useRecording}
                    onChange={() =>
                      recordingPackage &&
                      handlePackageChange(
                        'recording',
                        !userPreferences.useRecording,
                        recordingPackage
                      )
                    }
                  />
                </div>
                <span
                  className={`text-newspaper-black ml-auto text-3xl ${
                    !userPreferences.useRecording && recordingPackage.optional ? 'opacity-50' : ''
                  }`}
                >
                  {recordingPackage.price > 0
                    ? formatAmount(recordingPackage.price, projectCurrency)
                    : t('checkout.free')}
                </span>
              </div>
            )}

            {/* Pricing Summary */}
            <div className="mt-2 border-t-2 border-[#2a2a2a] pt-6">
              <div className="flex justify-between w-full mb-2">
                <span className="text-newspaper-black text-3xl">
                  {t('checkout.subtotal', 'Subtotal:')}
                </span>
                <span
                  className={`text-3xl ${transaction.discount ? 'line-through text-gray-500' : 'text-newspaper-black'}`}
                >
                  {formatAmount(calculateSubtotal(), projectCurrency)}
                </span>
              </div>

              {transaction.discount && transaction.discount > 0 ? (
                <div className="flex justify-between w-full mb-2">
                  <span className="text-green-600 text-3xl">
                    {t('checkout.discount', 'Discount:')}
                  </span>
                  <span className="text-green-600 text-3xl">
                    -{formatAmount(transaction.discount, projectCurrency)}
                  </span>
                </div>
              ) : (
                <div></div>
              )}

              <div className="flex justify-between w-full py-2 pt-6 border-t-2 border-[#2a2a2a] mt-6">
                <span className="font-bold text-newspaper-black text-3xl">
                  {t('checkout.total', 'Total:')}
                </span>
                <span className="font-bold text-newspaper-black text-3xl">
                  {formatAmount(calculateTotal(), projectCurrency)}
                </span>
              </div>
            </div>

            {/* Remaining Time Display */}
            <div
              className={`text-newspaper-black text-center text-3xl ${!remainingTime ? 'invisible' : ''}`}
            >
              {t('checkout.remainingTime', 'Remaining Time:')} {remainingTime}
            </div>

            <div className="flex flex-col gap-4 mt-2 items-center w-full">
              {(config?.settings.voucherEnabled ?? config?.settings.promoEnabled) && !showPromoInput && (
                <button
                  type="button"
                  onClick={() => setShowPromoInput(true)}
                  className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 hover:opacity-90 w-3/4 bg-[#fafaf8] text-[#1a1a1a] rounded-none shadow-none py-6 px-12 text-4xl"
                >
                  {t('checkout.havePromo', 'Have a voucher code?')}
                </button>
              )}

              <button
                type="button"
                onClick={handleNext}
                className="border-2 border-double border-[#2a2a2a] font-medium transition-all duration-200 hover:opacity-90 w-3/4 bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none py-6 px-12 text-4xl"
              >
                {paymentConfig?.settings.enabled
                  ? t('checkout.proceedToPayment', 'Proceed to Payment')
                  : t('common.continue', 'Continue')}
              </button>
            </div>
          </div>
        </div>

        {/* Step-by-Step Instructions - Only show if steps are configured */}
        {globalConfig?.customTheme?.checkoutSteps && globalConfig.customTheme.checkoutSteps.length > 0 && (
          <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-4 mt-32 w-full max-w-full mx-auto">
            <h2 className="font-serif font-bold leading-tight uppercase tracking-wider text-[#1a1a1a] mb-4 text-3xl">
              {t('newspaper.checkout.steps.title', 'How to Use')}
            </h2>
            <ol className="list-decimal list-inside text-newspaper-black font-serif space-y-2 text-3xl">
              {globalConfig.customTheme.checkoutSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {showPromoInput && (config?.settings.voucherEnabled ?? config?.settings.promoEnabled) && (
        <OnScreenKeyboard
          onEnter={handlePromoEnter}
          placeholder={t('checkout.voucherCodePlaceholder', 'Type or paste your code')}
          onClose={() => {
            setShowPromoInput(false)
            setPromoCode('')
          }}
          newspaperMode={true}
        />
      )}
    </NewspaperLayout>
  )
}

export default CheckoutPage
